'use client';

import React from 'react';
import Link from 'next/link';
import { HiOutlineCursorClick } from '@react-icons/all-files/hi/HiOutlineCursorClick';
import { HiOutlineLightningBolt } from '@react-icons/all-files/hi/HiOutlineLightningBolt';
import { HiOutlineColorSwatch } from '@react-icons/all-files/hi/HiOutlineColorSwatch';
import { HiOutlinePuzzle } from '@react-icons/all-files/hi/HiOutlinePuzzle';
import { HiOutlineClock } from '@react-icons/all-files/hi/HiOutlineClock';
import { HiOutlineViewGrid } from '@react-icons/all-files/hi/HiOutlineViewGrid';
import { HiOutlineHeart } from '@react-icons/all-files/hi/HiOutlineHeart';
import { HiOutlineStar } from '@react-icons/all-files/hi/HiOutlineStar';
import { HiOutlineClipboardList } from '@react-icons/all-files/hi/HiOutlineClipboardList';
import {
  StyledHero,
  StyledHeroInner,
  StyledH1,
  StyledSubtitle,
  StyledToolGrid,
  StyledToolGridInner,
  StyledToolCard,
  StyledCardFooter,
  StyledUseToolLink,
  StyledComingSoonBadge,
} from './EngagementHub.styles';

const TOOL_ICONS: Record<string, React.ReactNode> = {
  'engagement-score-calculator': <HiOutlineCursorClick />,
  'gamification-roi-calculator': <HiOutlineLightningBolt />,
  'venn-diagram-maker': <HiOutlineColorSwatch />,
  'quiz-lead-generator': <HiOutlinePuzzle />,
  'holiday-countdown': <HiOutlineClock />,
  'tier-list-maker': <HiOutlineViewGrid />,
  'alignment-chart-maker': <HiOutlineViewGrid />,
  'fundraising-thermometer-maker': <HiOutlineHeart />,
  'bingo-card-generator': <HiOutlineStar />,
  'holiday-trivia': <HiOutlinePuzzle />,
  'printable-bracket-maker': <HiOutlineViewGrid />,
  '8-team-bracket': <HiOutlineViewGrid />,
  '16-team-bracket': <HiOutlineViewGrid />,
  '32-team-bracket': <HiOutlineViewGrid />,
  '64-team-bracket': <HiOutlineViewGrid />,
  'blank-bracket': <HiOutlineViewGrid />,
  'single-elimination-bracket': <HiOutlineViewGrid />,
  'double-elimination-bracket': <HiOutlineViewGrid />,
  'consolation-bracket': <HiOutlineViewGrid />,
  'triple-elimination-bracket': <HiOutlineViewGrid />,
  'nfl-playoff-bracket': <HiOutlineViewGrid />,
  'college-football-playoff-bracket': <HiOutlineViewGrid />,
  'march-madness-bracket': <HiOutlineViewGrid />,
  'potluck-sign-up-sheet': <HiOutlineClipboardList />,
};

interface ITool {
  slug: string;
  name: string;
  description: string;
  comingSoon?: boolean;
}

interface IProps {
  tools: ITool[];
}

export const EngagementHub = ({ tools }: IProps) => {
  return (
    <>
      <StyledHero>
        <StyledHeroInner>
          <StyledH1>Free Engagement Tools</StyledH1>
          <StyledSubtitle>
            Free tools to measure engagement, project gamification ROI, estimate quiz-based
            lead generation, and build shareable tier lists that spark a reaction.
          </StyledSubtitle>
        </StyledHeroInner>
      </StyledHero>

      <StyledToolGrid>
        <StyledToolGridInner>
          {tools.map((tool) => (
            <StyledToolCard key={tool.slug} available={!tool.comingSoon}>
              <div>
                <span className="tool-icon">{TOOL_ICONS[tool.slug]}</span>
                <h2>{tool.name}</h2>
                <p>{tool.description}</p>
              </div>
              <StyledCardFooter>
                {!tool.comingSoon ? (
                  <StyledUseToolLink as={Link} href={`/free-tools/engagement/${tool.slug}`}>
                    Use Tool →
                  </StyledUseToolLink>
                ) : (
                  <StyledComingSoonBadge>Coming soon</StyledComingSoonBadge>
                )}
              </StyledCardFooter>
            </StyledToolCard>
          ))}
        </StyledToolGridInner>
      </StyledToolGrid>
    </>
  );
};
